import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RotatingText from "../../DefaultPage/DefaultPageComponents/RotateText/RotateText";
import "./ai-services.css";

// Import your visuals (replace with your actual video/image paths)
import videoVisual3 from "./avatar-video.mp4";
import videoVisual2 from "./ai-voice.mp4";
import videoVisual1 from "./ai-video.mp4";
import videoVisual4 from "./ai-visual.mp4";

const visuals = [
  videoVisual1,
  videoVisual2,
  videoVisual3,
  videoVisual4,
];

const AiServices = () => {
  const { t } = useTranslation();
  const videoRefs = useRef([]);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [magneticPosition, setMagneticPosition] = useState({ x: 0, y: 0 });
  const buttonRefs = useRef([]);

  // Get the services data from i18n
  const services = t("aiServicesPage.services.items", { returnObjects: true });

  // Scroll to form section
  const handleLearnMore = () => {
    const section = document.getElementById("form_section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleVideoHover = (index, isHovering) => {
    const video = videoRefs.current[index];
    if (video) {
      if (isHovering) {
        // Create a play promise to handle autoplay restrictions
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Video started playing
              video.muted = false; // Unmute after successful play
              setHoveredVideo(index);
            })
            .catch(error => {
              // Auto-play was prevented
              // Keep video muted and try playing
              video.muted = true;
              video.play().then(() => {
                setHoveredVideo(index);
              });
            });
        }
      } else {
        video.pause();
        video.muted = true;
        video.currentTime = 0;
        setHoveredVideo(null);
      }
    }
  };

  const handleMagneticMove = (e, idx) => {
    const button = buttonRefs.current[idx];
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const buttonCenterX = rect.left + rect.width / 2;
    const buttonCenterY = rect.top + rect.height / 2;

    const deltaX = e.clientX - buttonCenterX;
    const deltaY = e.clientY - buttonCenterY;
    const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);
    
    // Increased magnetic radius
    const maxDistance = 100; // Changed from 50 to 100

    if (distance < maxDistance) {
      const scale = 1 - distance / maxDistance;
      setMagneticPosition({
        // Increased movement range
        x: deltaX * scale * 0.8, // Changed from 0.5 to 0.8
        y: deltaY * scale * 0.8  // Changed from 0.5 to 0.8
      });
    } else {
      setMagneticPosition({ x: 0, y: 0 });
    }
  };

  const handleMagneticLeave = () => {
    setMagneticPosition({ x: 0, y: 0 });
  };

  return (
    <section id="ai_services_section">
      <h1 id="ai_services_hdng">{t("aiServicesPage.services.heading")}</h1>
      <p id="ai_services_txt">{t("aiServicesPage.services.text")}</p>
      <div id="ai_services_div">
        {services.map((service, idx) => (
          <div
            className={`ai_services_item_div${idx % 2 === 1 ? " ai_services_item_div_reverse" : ""}`}
            key={idx}
          >
            <div className="ai_services_item_txt_div">
              <h3 className="ai_services_item_hdng">{service.title}</h3>
              <p className="ai_services_item_desc" style={{ marginBottom: 18 }}>{service.description}</p>
              <div className="ai_services_rotating_text">
                <RotatingText texts={service.points} interval={5000} />
              </div>
              <button
                ref={el => buttonRefs.current[idx] = el}
                className="ai_services_learn_btn"
                onClick={handleLearnMore}
                onMouseMove={(e) => handleMagneticMove(e, idx)}
                onMouseLeave={handleMagneticLeave}
                style={{
                  marginTop: 24,
                  transform: `translate(${magneticPosition.x}px, ${magneticPosition.y}px)`,
                  transition: 'transform 0.2s ease-out'
                }}
              >
                {t("aiServicesPage.services.learnBtn", "Learn More")}
              </button>
            </div>
            <div 
              className="ai_services_item_visual_div"
              onMouseEnter={() => handleVideoHover(idx, true)}
              onMouseLeave={() => handleVideoHover(idx, false)}
            >
              {hoveredVideo !== idx && (
                <div className="ai_services_video_overlay">
                  <span>Hover to play</span>
                </div>
              )}
              <video
                ref={el => videoRefs.current[idx] = el}
                className="ai_services_item_visual"
                src={visuals[idx]}
                loop
                playsInline
                style={{ borderRadius: "12px", width: "100%" }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AiServices;
